import { DiscourseAnnotation, DiscourseOptions, TagIds, Tags } from "../common/page-data";
import { MersenneTwister } from "../utils/mersenne-twister";
import { removeRandomArrayElement, shuffleArray } from "../utils/utils";

export const generateAnnotationOptions = (
  random: MersenneTwister,
  annotations: DiscourseAnnotation[],
  tags: Tags,
  requiredCorrectTag?: string,
  requiredIncorrectTagToPair?: string
) => {
    // For each annotation, which is essentially a sub-question, we need to generate the options that
    // the user can select.
    const optionsPerAnnotation: DiscourseOptions = {};
    let maxDistractors = 4;
    for (const annotation of annotations) {
      // Add correct tag to list of options for this dialog/annotation
      const correctTagId = annotation.tag;
      const userOptions = [tags[correctTagId].id];

      // Create pool of potential distractor tags to add along with the correct tag. This will
      // help form the menu of options for the user to pick from.
      // Don't include the correct tag or responses similar to the correct tag
      const potentialTagIdOptions = [];
      for (const tagId in tags) {
        if (tagId !== correctTagId) {
          // Per feedback from Patrick, if the best answer is 'Link behavior...', don't also include
          // the 'Restate...' options as they can be too difficult to mix up.
          if (correctTagId === TagIds.linkBehavior &&
            (tagId === TagIds.restateExperience || tagId === TagIds.restateIssues)) {
            continue;
          }

          potentialTagIdOptions.push(tagId);
        }
      }

      // If there is a specific incorrect tag that should be paired with a correct one,
      // force it to be added to returned set of user options and remove it from potential list to
      // randomly pick from since we already added it here.
      if (correctTagId === requiredCorrectTag && requiredIncorrectTagToPair) {
        userOptions.push(requiredIncorrectTagToPair);
        maxDistractors--;
        const indexToRemove = potentialTagIdOptions.indexOf(requiredIncorrectTagToPair);
        if (indexToRemove >= 0) {
          potentialTagIdOptions.splice(indexToRemove, 1);
        }
      }

      // Pick out additional distractor tags from the pool
      const totalDistractors = Math.min(maxDistractors, potentialTagIdOptions.length);
      for (let i = totalDistractors; i; --i) {
        const tagId = removeRandomArrayElement(potentialTagIdOptions, random);
        userOptions.push(tagId);
      }

      shuffleArray(userOptions, random);
      optionsPerAnnotation[annotation.id] = userOptions;
    }

    return optionsPerAnnotation;
}